/**
 * LibrarySidebar — pop-out library panel for the Edit-with-AI drawer.
 *
 * Three tabs: Personas / Skills / Tools. Tabs replace the prior stacked
 * sections so each tab can fill the panel with its own scroll. The Tools
 * tab adds a hover-revealed detail panel docked at the bottom that shows
 * the hovered tool's `long` description verbatim.
 *
 * JSX layout (tabs, search, list, footer, detail panel) copied verbatim
 * from designs/tool-discovery-options.html — the `Sidebar` function
 * component there is the binding spec. Personas + skills row JSX matches
 * what the design shows for those tabs (PersonaRow / SkillRow).
 *
 * Open state is parent-owned: when `open === false` only the persistent
 * 32px rail renders; the 300px overlay panel only appears when `open`.
 */

import { useDeferredValue, useState } from 'react';
import type { KnownSurface } from '../../lib/skill-surfaces-client';
import type { PersonaListItem } from '../../types/personas';
import type { SkillListItem } from '../../types/skills';
import type { ToolListItem } from '@shared/schemas/tools';
import { usePersonas } from './hooks/usePersonas';
import { useSkills } from './hooks/useSkills';
import { PersonaRow } from './PersonaRow';
import { SkillRow } from './SkillRow';
import { ToolRow } from './ToolRow';

export interface LibrarySidebarProps {
  /** The currently locked persona; null means unlocked (pre-chat). */
  persona: PersonaListItem | null;
  onPickPersona: (p: PersonaListItem) => void;
  onPickSkill: (s: SkillListItem) => void;
  onBrowseAll?: (tab: 'personas' | 'skills') => void;
  /**
   * Tools registry — owned by the parent (EditWithAIView) so a single
   * `useTools()` subscriber feeds both the sidebar and the chat surface.
   */
  tools: ToolListItem[];
  /** Parent-owned: true renders the overlay panel, false renders the inline rail. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Optional surface filter. When supplied, `usePersonas` and `useSkills` are
   * called with this surface so the server narrows the list to items tagged for
   * that surface (U2). When omitted the full unfiltered lists are returned,
   * preserving existing Edit-with-AI behaviour.
   */
  surface?: KnownSurface;
}

type TabKey = 'personas' | 'skills' | 'tools';

const TAB_META: Record<
  TabKey,
  {
    icon: string;
    label: string;
    color: 'ube' | 'blueberry' | 'tangerine';
    trigger: '@' | '/' | null;
    subtitle: string;
  }
> = {
  personas: {
    icon: '🪄',
    label: 'Personas',
    color: 'ube',
    trigger: '@',
    subtitle: 'pick 1',
  },
  skills: {
    icon: '⚡',
    label: 'Skills',
    color: 'blueberry',
    trigger: '/',
    subtitle: 'use anytime',
  },
  tools: {
    icon: '⚙',
    label: 'Tools',
    color: 'tangerine',
    trigger: null,
    subtitle: 'model picks from these',
  },
};

const COLOR_ACTIVE: Record<TabKey, string> = {
  personas: 'text-ube-700 border-ube-500 dark:text-ube-400',
  skills: 'text-blueberry-700 border-blueberry-500 dark:text-blueberry-400',
  tools: 'text-tangerine-700 border-tangerine-500 dark:text-tangerine-400',
};

const COLOR_HEADING: Record<TabKey, string> = {
  personas: 'text-ube-600 dark:text-ube-400',
  skills: 'text-blueberry-600 dark:text-blueberry-400',
  tools: 'text-tangerine-600 dark:text-tangerine-400',
};

function matches(query: string, ...fields: (string | undefined | null)[]) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  for (const f of fields) {
    if (f && f.toLowerCase().includes(q)) return true;
  }
  return false;
}

export function LibrarySidebar({
  persona,
  onPickPersona,
  onPickSkill,
  onBrowseAll,
  tools,
  open,
  onOpenChange,
  surface,
}: LibrarySidebarProps) {
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [activeTab, setActiveTab] = useState<TabKey>('personas');
  const [hoveredTool, setHoveredTool] = useState<ToolListItem | null>(null);

  // Opens in a NEW TAB (Spencer QA, 2026-08-20): the skill/persona editor is
  // a separate, dedicated page (Edit-with-AI), not part of this chat dock.
  // Same-tab navigate() replaced the whole chat view underneath with no
  // visual cue that you'd left it — confusing mid-chat. A new tab makes the
  // "this is a different window" boundary obvious and leaves the chat dock
  // exactly as it was when you come back.
  const handleCreateNew = (key: TabKey) => {
    if (key === 'skills') window.open('/skills/new', '_blank', 'noopener,noreferrer');
    else if (key === 'personas') window.open('/personas/new', '_blank', 'noopener,noreferrer');
  };

  const personasQuery = usePersonas(surface);
  const skillsQuery = useSkills(surface);

  const personas = personasQuery.data ?? [];
  const skills = skillsQuery.data ?? [];

  const filteredPersonas = personas.filter((p) =>
    matches(deferredQuery, p.name, p.description),
  );
  const filteredSkills = skills.filter((s) =>
    matches(deferredQuery, s.name, s.description),
  );
  const filteredTools = tools.filter((t) => matches(deferredQuery, t.label, t.short));

  const tabs: TabKey[] = ['personas', 'skills', 'tools'];
  const counts: Record<TabKey, number> = {
    personas: personas.length,
    skills: skills.length,
    tools: tools.length,
  };

  const meta = TAB_META[activeTab];
  const locked = persona !== null;

  // Switching tabs always clears the hovered tool (detail panel is
  // Tools-only). Wrap setActiveTab to enforce.
  const handleTabClick = (key: TabKey) => {
    if (key !== activeTab) {
      setHoveredTool(null);
      setActiveTab(key);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        className="w-8 border-l border-nightshade-200 dark:border-nightshade-700 bg-nightshade-50 dark:bg-nightshade-800 hover:bg-nightshade-100 dark:hover:bg-nightshade-700 flex flex-col items-center justify-center gap-2 text-nightshade-500 dark:text-nightshade-400 flex-shrink-0 relative z-30"
        title={open ? 'Close toolkit' : 'Open toolkit'}
        aria-label={open ? 'Close toolkit sidebar' : 'Open toolkit sidebar'}
        aria-expanded={open}
      >
        <span className="text-xs">{open ? '›' : '‹'}</span>
        <span className="text-[10px]" style={{ writingMode: 'vertical-rl' }}>
          Toolkit
        </span>
      </button>

      {open && (
        <div className="absolute inset-y-0 right-8 z-20 w-[min(300px,calc(100%-2rem))] border-l border-nightshade-200 dark:border-nightshade-700 bg-nightshade-50 dark:bg-nightshade-800 flex flex-col shadow-[-8px_0_20px_rgba(0,0,0,0.06)]">
          {/* Header */}
          <div className="px-3 py-2.5 border-b border-nightshade-200 dark:border-nightshade-700 flex items-center justify-between">
            <div className="text-[11px] font-semibold text-nightshade-700 dark:text-nightshade-300 uppercase tracking-wide">
              Toolkit
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="text-[14px] leading-none text-nightshade-400 hover:text-nightshade-700 dark:text-nightshade-500 dark:hover:text-nightshade-100 px-1"
              aria-label="Close toolkit"
            >
              ×
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-nightshade-200 dark:border-nightshade-700 bg-white dark:bg-nightshade-900">
            {tabs.map((key) => {
              const t = TAB_META[key];
              const isActive = key === activeTab;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleTabClick(key)}
                  className={`flex-1 text-[11px] font-semibold uppercase tracking-wide py-2 transition-colors ${
                    isActive
                      ? `${COLOR_ACTIVE[key]} border-b-2 bg-nightshade-50 dark:bg-nightshade-800`
                      : 'text-nightshade-500 hover:text-nightshade-700 dark:text-nightshade-400 dark:hover:text-nightshade-300 border-b-2 border-transparent'
                  }`}
                >
                  <span className="mr-1">{t.icon}</span>
                  {t.label}
                  <span className="text-[9px] ml-1 opacity-60">{counts[key]}</span>
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="p-2.5 border-b border-nightshade-200 dark:border-nightshade-700">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              type="text"
              placeholder="Search toolkit…"
              className="w-full text-xs px-2 py-1.5 bg-white dark:bg-nightshade-900 border border-nightshade-200 dark:border-nightshade-700 text-content-primary dark:text-nightshade-100 rounded focus:outline-none focus:border-blueberry-400 dark:focus:border-blueberry-600"
            />
          </div>

          {/* Section meta */}
          <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
            <div className="flex items-center gap-1.5">
              <span
                className={`text-[10px] font-semibold uppercase tracking-wider ${COLOR_HEADING[activeTab]}`}
              >
                {meta.icon} {meta.label}
              </span>
              <span className="text-[10px] text-nightshade-400 dark:text-nightshade-500 normal-case font-normal">
                {meta.subtitle}
              </span>
            </div>
            {meta.trigger && <span className="text-[10px] kbd">{meta.trigger}</span>}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto px-1.5 pb-2 scroll-pane">
            {activeTab === 'personas' && (
              <>
                {personasQuery.isPending && (
                  <div className="text-[10px] text-nightshade-400 dark:text-nightshade-500 px-2 py-1">Loading…</div>
                )}
                {personasQuery.isError && (
                  <div className="text-[10px] text-content-danger px-2 py-1">
                    Couldn&rsquo;t load personas.
                  </div>
                )}
                {!personasQuery.isPending &&
                  !personasQuery.isError &&
                  filteredPersonas.length === 0 && (
                    <div className="text-[10px] text-nightshade-400 dark:text-nightshade-500 px-2 py-1">
                      {query ? 'No matches.' : 'No personas yet.'}
                    </div>
                  )}
                <div className="space-y-0.5">
                  {filteredPersonas.map((p) => (
                    <PersonaRow
                      key={p.id}
                      p={p}
                      selected={persona?.id === p.id}
                      locked={locked}
                      onClick={() => !locked && onPickPersona(p)}
                    />
                  ))}
                </div>
              </>
            )}

            {activeTab === 'skills' && (
              <>
                {skillsQuery.isPending && (
                  <div className="text-[10px] text-nightshade-400 dark:text-nightshade-500 px-2 py-1">Loading…</div>
                )}
                {skillsQuery.isError && (
                  <div className="text-[10px] text-content-danger px-2 py-1">
                    Couldn&rsquo;t load skills.
                  </div>
                )}
                {!skillsQuery.isPending &&
                  !skillsQuery.isError &&
                  filteredSkills.length === 0 && (
                    <div className="text-[10px] text-nightshade-400 dark:text-nightshade-500 px-2 py-1">
                      {query ? 'No matches.' : 'No skills yet.'}
                    </div>
                  )}
                <div className="space-y-0.5">
                  {filteredSkills.map((s) => (
                    <SkillRow key={s.id} s={s} onClick={() => onPickSkill(s)} />
                  ))}
                </div>
              </>
            )}

            {activeTab === 'tools' && (
              <>
                {filteredTools.length === 0 && (
                  <div className="text-[10px] text-nightshade-400 dark:text-nightshade-500 px-2 py-1">
                    {query ? 'No matches.' : 'No tools available.'}
                  </div>
                )}
                <div className="space-y-0.5">
                  {filteredTools.map((t) => (
                    <ToolRow
                      key={t.slug}
                      t={t}
                      hovered={hoveredTool?.slug === t.slug}
                      onHover={setHoveredTool}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-nightshade-200 dark:border-nightshade-700 px-3 py-2 flex items-center gap-2 text-[10px] text-nightshade-500 dark:text-nightshade-400">
            {activeTab === 'tools' ? (
              <>
                <span className="text-nightshade-400 dark:text-nightshade-500">⚙</span>
                <span>Tools are server-defined</span>
                {onBrowseAll && (
                  <button
                    type="button"
                    className="ml-auto text-nightshade-600 hover:text-nightshade-900 dark:text-nightshade-400 dark:hover:text-nightshade-100 cursor-pointer bg-transparent border-0 p-0"
                    // No browse-all for tools yet (deferred to a later iteration);
                    // the link is here for visual parity but is a no-op.
                    onClick={() => {}}
                  >
                    Browse all →
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleCreateNew(activeTab)}
                  className="text-nightshade-700 hover:text-nightshade-900 dark:text-nightshade-300 dark:hover:text-nightshade-100 cursor-pointer bg-transparent border-0 p-0"
                >
                  + New {activeTab.slice(0, -1)}
                </button>
                {onBrowseAll && (
                  <>
                    <span className="text-nightshade-300 dark:text-nightshade-600">·</span>
                    <button
                      type="button"
                      onClick={() => onBrowseAll(activeTab)}
                      className="text-nightshade-600 hover:text-nightshade-900 dark:text-nightshade-400 dark:hover:text-nightshade-100 cursor-pointer bg-transparent border-0 p-0"
                    >
                      Browse all →
                    </button>
                  </>
                )}
              </>
            )}
          </div>

          {/* Hover detail panel — only on Tools tab */}
          {activeTab === 'tools' && hoveredTool && (
            <div className="border-t border-nightshade-200 dark:border-nightshade-700 bg-white dark:bg-nightshade-900 px-3 py-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-sm">{hoveredTool.icon}</span>
                <div className="text-[11px] font-semibold text-nightshade-900 dark:text-nightshade-100">
                  {hoveredTool.label}
                </div>
                <span className="text-[10px] text-nightshade-400 dark:text-nightshade-500 font-mono ml-auto">
                  {hoveredTool.slug}
                </span>
              </div>
              <div className="text-[11px] text-nightshade-700 dark:text-nightshade-300 leading-snug">
                {hoveredTool.long}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
